// ============================================================================
// CLEO'S SECRET — ConfirmationModal (shared)
// File: src/components/ConfirmationModal.jsx
//
// Generic confirmation dialog. First consumer is the grace-cancel flow
// (Phase 3), but the shape is meant to be reusable for any
// premise-headline-two-CTA decision surface.
//
// Brand contract (per docs/design-spec.md + memory feedback_calm_cta_over_urgency):
//   - Plum-tinted backdrop rgba(29, 17, 22, 0.55) — never pure black
//     (vignette-on-plum rule). Tap-to-dismiss only when not busy.
//   - Sand-coloured panel #E8DDC4, max-width 540, SAND3 hairline border,
//     no box-shadow.
//   - Italic premise (T.premise, P.BR2)
//   - Headline (Cormorant, P.INK, italic span allowed via children)
//   - Optional body line below headline
//   - Two equally-weighted CTAs side-by-side at >540px viewport, stacked
//     below. Same size, fill differs (confirm = filled gold, cancel = ghost).
//   - Inline error slot (T.bodySmItalic, P.RED)
//   - Busy state: confirm button disabled + label "Cancelling..." +
//     small rotating diamond.
//   - Closes via Escape key, backdrop click, programmatic onClose.
//   - Escape and backdrop click are no-ops while busy.
//
// Props:
//   open           bool      — render or not
//   onClose        () => void
//   premise        string    — italic gold premise above the title
//   title          ReactNode — headline; pass <em> for italic spans
//   body           ReactNode — optional body paragraph
//   confirmLabel   string
//   cancelLabel    string
//   busyLabel      string    — defaults to 'Working...'
//   onConfirm      () => void
//   busy           bool      — disables confirm + lockouts dismissal paths
//   error          string    — inline error message
//   successMessage ReactNode — when present, replaces buttons + body with
//                              this success state. Parent dismisses via
//                              setTimeout after settle.
// ============================================================================

import React, { useEffect } from 'react';
import { FF, P, T } from '../lib/design-tokens';

const BACKDROP = 'rgba(29, 17, 22, 0.55)';

// Shared button shape — keeps confirm + cancel exactly the same size per
// spec § 11 ("All adjacent buttons same size. Primary (filled) and
// secondary (outline) CTAs in the same area share dimensions").
const SHARED_BTN_STYLE = {
  flex: 1,
  minHeight: 56,
  padding: '16px 24px',
  border: '1.5px solid ' + P.GO,
  fontFamily: FF,
  fontSize: 'clamp(15px, 1.3vw, 18px)',
  fontStyle: 'italic',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background 0.18s ease, opacity 0.18s ease',
};

function Diamond({ size = 8, color, opacity = 1, spinning = false }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block', width: size, height: size,
        background: color || P.BR, transform: spinning ? undefined : 'rotate(45deg)', opacity,
        flexShrink: 0,
        animation: spinning ? 'cleo-rotate-diamond 6s linear infinite' : undefined,
      }}
    />
  );
}

export default function ConfirmationModal(props) {
  const {
    open,
    onClose,
    premise,
    title,
    body,
    confirmLabel,
    cancelLabel,
    busyLabel = 'Working...',
    onConfirm,
    busy = false,
    error,
    successMessage,
  } = props;

  // Escape closes (no-op while busy).
  useEffect(function() {
    if (!open) return;
    function onKey(e) {
      if (e.key === 'Escape' && !busy && onClose) onClose();
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [open, busy, onClose]);

  // Lock body scroll while open.
  useEffect(function() {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return function() { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  function handleBackdrop(e) {
    if (e.target !== e.currentTarget) return;
    if (busy) return;
    if (onClose) onClose();
  }

  return (
    <div
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: BACKDROP,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 40px)',
      }}
    >
      <div
        onClick={function(e) { e.stopPropagation(); }}
        style={{
          background: P.SAND,
          border: '1px solid ' + P.SAND3,
          maxWidth: 540, width: '100%',
          padding: 'clamp(28px, 4vw, 44px) clamp(24px, 4vw, 40px)',
          textAlign: 'center',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {premise && (
          <p style={{
            ...T.premise,
            fontSize: 'clamp(18px, 1.9vw, 24px)',
            color: P.BR2,
            margin: '0 0 16px',
          }}>{premise}</p>
        )}

        {title && (
          <h2 style={{
            ...T.display,
            fontSize: 'clamp(28px, 3.4vw, 38px)',
            color: P.INK,
            margin: '0 0 18px',
            lineHeight: 1.2,
          }}>{title}</h2>
        )}

        {!successMessage && body && (
          <div style={{
            ...T.bodyMd,
            color: P.TL,
            margin: '0 auto 28px',
            maxWidth: 440,
          }}>{body}</div>
        )}

        {successMessage ? (
          <div style={{ marginTop: 8 }}>{successMessage}</div>
        ) : (
          <React.Fragment>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 8,
            }}>
              <button
                onClick={busy ? undefined : (onClose || function() {})}
                disabled={busy}
                style={Object.assign({}, SHARED_BTN_STYLE, {
                  background: 'transparent',
                  color: P.BR2,
                  opacity: busy ? 0.55 : 1,
                  cursor: busy ? 'default' : 'pointer',
                })}
                onMouseEnter={function(e) {
                  if (busy) return;
                  e.currentTarget.style.background = 'rgba(201, 169, 97, 0.06)';
                }}
                onMouseLeave={function(e) {
                  if (busy) return;
                  e.currentTarget.style.background = 'transparent';
                }}
              >{cancelLabel}</button>

              <button
                onClick={busy ? undefined : onConfirm}
                disabled={busy}
                style={Object.assign({}, SHARED_BTN_STYLE, {
                  background: busy ? 'rgba(201, 169, 97, 0.45)' : P.GO,
                  color: P.INK,
                  cursor: busy ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                })}
                onMouseEnter={function(e) {
                  if (busy) return;
                  e.currentTarget.style.background = P.GO2;
                }}
                onMouseLeave={function(e) {
                  if (busy) return;
                  e.currentTarget.style.background = P.GO;
                }}
              >
                {busy && <Diamond size={9} color={P.INK} opacity={0.85} spinning />}
                <span>{busy ? busyLabel : confirmLabel}</span>
              </button>
            </div>

            {error && (
              <div style={{
                ...T.bodySmItalic,
                color: P.RED,
                marginTop: 18,
              }}>{error}</div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
